
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../integrations/supabase/client';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Database } from '../../integrations/supabase/types';

type BlogPost = Database['public']['Tables']['blog_posts']['Row'];

const BlogList = () => {
  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['public-blog-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('published', true)
        .order('published_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching blog posts:', error);
        throw error;
      }
      return data as BlogPost[];
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    console.error('Blog posts error:', error);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Unable to load blog posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-r from-secondary to-secondary-dark text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">Latest Updates & Insights</h1>
          <p className="text-lg text-gray-200">
            Stay informed about luxury transportation and travel tips
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts?.map((post) => (
            <article 
              key={post.id} 
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              {post.image_url && (
                <div className="aspect-w-16 aspect-h-9">
                  <img 
                    src={post.image_url} 
                    alt={post.title} 
                    className="w-full h-48 object-cover"
                  />
                </div>
              )}
              <div className="p-4">
                <h2 className="text-lg font-semibold mb-2 text-gray-900 line-clamp-1">
                  {post.title}
                </h2>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {post.excerpt}
                </p>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">
                    {new Date(post.published_at || '').toLocaleDateString()}
                  </span>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center text-primary hover:text-primary-dark transition-colors"
                  >
                    Read More <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlogList;
